import { Link } from 'react-router-dom';
import { Category } from 'api/categories/_requests';
import { useEffect, useRef, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons';

interface Props {
  total: number;
  categoryTypesCategory: Category[];
  selectedCatId: string;
  onCategoryClick: (category: Category) => void;
}

const SCROLL_STEP = 280; // px – tweak to taste

const CategoryTypeWiseCatItem = ({
  total,
  categoryTypesCategory,
  onCategoryClick,
  selectedCatId
}: Props) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  const scrollBy = (delta: number) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: delta, behavior: 'smooth' });
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const handleWheel = (e: WheelEvent) => {
      if (e.deltaY === 0) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
      updateArrows();
    };

    el.addEventListener('wheel', handleWheel, { passive: false });
    el.addEventListener('scroll', updateArrows); // keep in sync
    updateArrows(); // initial render

    return () => {
      el.removeEventListener('wheel', handleWheel);
      el.removeEventListener('scroll', updateArrows);
    };
  }, [updateArrows]);

  return (
    <div className="position-relative">
      <div
        ref={scrollRef}
        className="overflow-auto pe-2 py-3 d-flex gap-3"
        style={{
          scrollSnapType: 'x mandatory',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none'
        }}
      >
        <style>{`
          div::-webkit-scrollbar { display: none; }
        `}</style>
        {categoryTypesCategory.map(category => (
          <Link
            key={category._id}
            to="#"
            onClick={() => onCategoryClick(category)}
            className={`flex-shrink-0 text-decoration-none shadow-sm ${
              selectedCatId === category._id ? 'border border-primary' : ''
            }`}
            style={{
              scrollSnapAlign: 'start',
              width: '150px',
              transition: 'transform 0.3s ease'
            }}
          >
            <div
              className="p-0 d-flex flex-column align-items-center text-center border rounded overflow-hidden bg-white"
              style={{ height: '180px' }}
            >
              <div
                style={{
                  height: '140px',
                  width: '100%',
                  backgroundColor: '#f9f9f9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderBottom: '1px solid #eee'
                }}
              >
                <img
                  src="https://cdn.moglix.com/cms/element/Images_2020-07-21_14-35-04_Images_2020-04-30_03-10-15_safety@2x.png"
                  alt={category.name}
                  style={{ maxHeight: '120px', objectFit: 'contain' }}
                  loading="lazy"
                />
              </div>
              <div
                className="px-2 py-2 w-100"
                style={{ backgroundColor: '#fff' }}
              >
                <h6 className="mb-0 fw-semibold text-muted text-break small">
                  {category.name}
                </h6>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {canScrollLeft && (
        <button
          aria-label="Scroll left"
          className="btn btn-dark position-absolute top-50 start-0 translate-middle-y shadow-sm p-0 d-flex align-items-center justify-content-center"
          style={{
            width: '38px',
            height: '38px',
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            color: '#fff',
            border: 'none',
            zIndex: 10
          }}
          onClick={() => scrollBy(-SCROLL_STEP)}
        >
          <FontAwesomeIcon icon={faChevronLeft} />{' '}
        </button>
      )}

      {canScrollRight && (
        <button
          aria-label="Scroll right"
          className="btn btn-dark position-absolute top-50 end-0 translate-middle-y shadow-sm  p-0 d-flex align-items-center justify-content-center"
          style={{
            width: '38px',
            height: '38px',
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            color: '#fff',
            border: 'none',
            zIndex: 10
          }}
          onClick={() => scrollBy(SCROLL_STEP)}
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </button>
      )}
    </div>
  );
};

export default CategoryTypeWiseCatItem;
